import { FC, ReactElement, ReactNode, createContext, useContext } from "react";
import { Navigate, Route, Routes } from "react-router";

import { UserLoggedIn } from "@nens/brostar/openapi-client";

import { useUserLoggedInQuery } from "../queries/query-auth";

// Note the hack with null as User is acceptable because the context will only be
// supplied by the Provider in situations where profile is a User, not null.
// Saves a lot of '!' use everywhere the context is actually used.
export const AuthContext = createContext<{ profile: UserLoggedIn }>({
  profile: null as unknown as UserLoggedIn,
});

interface AuthProviderProps {
  fetchingComponent: ReactElement;
  publicRoutes: ReactElement;
  children?: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ fetchingComponent, publicRoutes, children }) => {
  const { status, data } = useUserLoggedInQuery();

  if (status === "error") return null;

  if (status !== "success") return fetchingComponent;

  if (data.loginUrl) {
    return (
      <AuthContext.Provider value={{ profile: data }}>
        {publicRoutes}
        <Routes>
          <Route path="/public/*" element={null} />
          <Route path="*" element={<Navigate to="/public" replace />} />
        </Routes>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ profile: data }}>
      {publicRoutes}
      {children}
    </AuthContext.Provider>
  );
};

// Helper hook to get access to user from anywhere in the children tree of AuthContext.Provider.
export function useUser() {
  const context = useContext(AuthContext);
  return context && context.profile;
}

export { AuthProvider };
